import React from 'react';
import Swal, { SweetAlertResult } from 'sweetalert2';
import { Link } from 'react-router-dom';
import { FaAngleLeft } from 'react-icons/fa';
import { settings } from '../../../settings';
import Fetch from '../../../utils/fetch';
import { ReqEnum } from '../../../const/requestStatus';
import Button, {
  ButtonGroup,
  LinkButton,
} from '../../../components/buttons/Button';
import {
  Order,
  OrderStatus,
  Region,
  UpdateOrderRequest,
} from '../../../types/orderTypes';
import { updateOrderRequest } from '../../orders/OrdersUtil';
import { BundleJson } from '../../../types/bundleTypes';
import { publishDraft } from '../../oldPlanner/utils/bundleDraft';
import ModalComponent from '../../../ReusableComponents/Modal/ModalComponent';
import DeviationForm from '../journey/deviations/DeviationForm';

const CANCEL_IS_ENABLED: OrderStatus[] = [
  OrderStatus.Accepted,
  OrderStatus.Ready,
];

const EDIT_IS_ENABLED: OrderStatus[] = [
  OrderStatus.Accepted,
  OrderStatus.InDelivery,
  OrderStatus.Delivered,
  OrderStatus.Ready,
];

const MARK_AS_READY_IS_ENABLED: OrderStatus[] = [
  OrderStatus.Accepted,
  OrderStatus.InDelivery,
  OrderStatus.Delivered,
  OrderStatus.Ready,
  OrderStatus.Canceled,
];

const MARK_AS_PICKED_UP_IS_ENABLED: OrderStatus[] = [OrderStatus.Accepted];

const MARK_AS_DELIVERED_IS_ENABLED: OrderStatus[] = [OrderStatus.InDelivery];

const MANUALLY_CREATE_BUNDLE_IS_ENABLED: OrderStatus[] = [OrderStatus.Ready];

interface Props {
  order: Order;
  onEditOrder: () => void;
  onDuplicateOrder: () => void;
  bundleId: string;
  editingOrder: boolean;
  onOrderStateChanged: () => void;
  region: Region;
}

type OrderAction =
  | 'mark_as_ready'
  | 'mark_as_picked'
  | 'mark_as_delivered'
  | 'mark_as_canceled';

interface State {
  manuallyCreateBundleAsync: {
    status: ReqEnum;
    data: BundleJson;
  };
  markOrderAsync: {
    status: ReqEnum;
    orderAction: OrderAction;
  };
  createDeviationModalOpen: boolean;
}

export default class OrderMenuBar extends React.Component<Props, State> {
  constructor(props) {
    super(props);

    this.state = {
      markOrderAsync: {
        status: ReqEnum.INIT,
        orderAction: null,
      },
      manuallyCreateBundleAsync: {
        status: ReqEnum.INIT,
        data: null,
      },
      createDeviationModalOpen: false,
    };
  }

  componentDidUpdate() {
    if (this.state.manuallyCreateBundleAsync.data) {
      window.location.href = `/orders/${this.props.order.id}`;
    }
  }

  markOrderAsPicked = () => {
    this.markOrderAs('mark_as_picked', 'mark-as-picked');
  };

  markOrderAsDelivered = async () => {
    const { order } = this.props;
    const confirm = await Swal.fire({
      title: 'Confirm mark as delivered',
      text: `Please confirm that order ${order.id} should be marked as delivered`,
      icon: 'warning',
      showCancelButton: true,
    });
    if (confirm.value) {
      this.markOrderAs('mark_as_delivered', 'mark-as-delivered');
    }
  };

  markOrderAsReady = async () => {
    const { bundleId, order } = this.props;
    if (bundleId) {
      const isDelivered = order.status === OrderStatus.Delivered;
      const confirm = await Swal.fire({
        title: isDelivered
          ? 'Order is already delivered'
          : 'Order is part of a bundle',
        text: isDelivered
          ? 'This order is marked as delivered. Marking this order as ready will reset the order status, remove it from the bundle and possibly change the bundle status or delete the bundle. Do you wish to continue?'
          : 'This order is part of a bundle. Marking this order as "ready" will remove it from the bundle and possibly change the bundle status or delete the bundle. Do you wish to continue?',
        icon: 'warning',
        showCancelButton: true,
      });
      if (confirm.value) {
        this.markOrderAs('mark_as_ready', 'mark-as-ready');
      }
    } else if (order.status === OrderStatus.Canceled) {
      const confirm = await Swal.fire({
        title: 'Order is cancelled',
        text: 'This order has been cancelled. Marking this order as ready will mark it as to be delivered again. Do you wish to continue?',
        icon: 'warning',
        showCancelButton: true,
      });
      if (confirm.value) {
        this.markOrderAs('mark_as_ready', 'mark-as-ready');
      }
    } else {
      this.markOrderAs('mark_as_ready', 'mark-as-ready');
    }
  };

  markOrderAsCanceled = (note: string) => {
    const body = {
      note: note,
    };
    this.markOrderAs('mark_as_canceled', 'mark-as-canceled', body);
  };

  markOrderAs = (orderAction: OrderAction, urlAction: string, body?: any) => {
    const { id } = this.props.order;
    const { onOrderStateChanged } = this.props;

    this.setState({
      markOrderAsync: {
        status: ReqEnum.PENDING,
        orderAction,
      },
    });

    Fetch.put({
      url: `${settings.newApiUrl}/order/${id}/${urlAction}`,
      body: body,
    })
      .then(response => {
        if (response.ok) {
          this.setState({
            markOrderAsync: {
              status: ReqEnum.SUCCESS,
              orderAction,
            },
          });
          onOrderStateChanged();
        }
        throw Error();
      })
      .catch(() =>
        this.setState({
          markOrderAsync: {
            status: ReqEnum.ERROR,
            orderAction,
          },
        })
      );
  };

  createDraftBundle = async (
    orderId: string,
    region: Region
  ): Promise<BundleJson> => {
    const instructions = [
      { type: 'PICKUP', orderId: orderId },
      { type: 'DELIVERY', orderId: orderId },
    ];

    const response = await Fetch.post({
      url: `${settings.newApiUrl}/bundle?region=${region}`,
      body: instructions,
    });
    if (response.ok) {
      return await response.json();
    } else if (response.status === 422) {
      const errors = await response.json();
      const message =
        errors && errors.length > 0
          ? errors.map(e => e.message).join('\n')
          : '';
      Swal.fire('Bundle could not be created', message, 'error');
      return null;
    } else {
      return null;
    }
  };

  manuallyCreateBundle = async () => {
    this.setState({
      manuallyCreateBundleAsync: { status: ReqEnum.PENDING, data: null },
    });

    const draftBundle = await this.createDraftBundle(
      this.props.order.id,
      this.props.order.region
    );
    const publishedBundle = draftBundle && (await publishDraft(draftBundle.id));

    if (publishedBundle) {
      this.setState({
        manuallyCreateBundleAsync: {
          status: ReqEnum.SUCCESS,
          data: publishedBundle,
        },
      });
    } else {
      this.setState({
        manuallyCreateBundleAsync: { status: ReqEnum.ERROR, data: null },
      });
    }
  };

  onClickCancelOrder = async () => {
    const { bundleId } = this.props;

    let text = `Are you sure you want to delete order ${this.props.order.id}? This is an irreversible action.`;

    if (bundleId) {
      text = `${text}
        <br/><br/>
        <p><strong>This order is part of a bundle. Cancelling it will remove it from the bundle.</strong></p>
        <p>Depending on the state of the bundle, it will be:</p>
        <ul>
          <li>Deleted if this order is the only one in it.</li>
          <li>Completed if all the other orders in the bundle is complete.</li>
        </ul>
      `;
    }

    const message: SweetAlertResult<string> = await Swal.fire({
      input: 'textarea',
      icon: 'warning',
      html: `<span class="content u-text-left">${text}</span>`,
      title: 'Cancel order',
      inputPlaceholder: 'Type your reason for cancelling here...',
      showCancelButton: true,
      confirmButtonText: 'Cancel order',
      confirmButtonColor: '#d33',
      cancelButtonText: 'Abort',
      cancelButtonColor: '#ccc',
    });
    if (message.value) {
      this.markOrderAsCanceled(message.value);
      return;
    }
    return Swal.fire('Your order is safe!');
  };

  render() {
    const { order, editingOrder, onDuplicateOrder } = this.props;
    const { markOrderAsync, manuallyCreateBundleAsync } = this.state;
    return (
      <div className="orders__menu-bar">
        <Link
          className="link__clean"
          style={{ display: 'flex', alignItems: 'center', width: 200 }}
          to="/orders"
        >
          <FaAngleLeft style={{ width: 20, height: 20, color: '#3a3d4b' }} />
          Back to orders
        </Link>
        <div style={{ height: 10 }} />
        <div>
          <div className="menu-bar__1">
            <ButtonGroup>
              <Button
                title="Create bundle"
                onClick={() => this.manuallyCreateBundle()}
                loading={manuallyCreateBundleAsync.status === ReqEnum.PENDING}
                disabled={
                  !MANUALLY_CREATE_BUNDLE_IS_ENABLED.includes(order.status) ||
                  editingOrder
                }
              />
              <LinkButton
                to={`/bundles/${this.props.bundleId}`}
                disabled={!this.props.bundleId || editingOrder}
              >
                Go to bundle
              </LinkButton>
            </ButtonGroup>

            <div className="mobile-only" style={{ height: 10 }} />
            <ButtonGroup>
              <Button
                title="Report deviation"
                onClick={() =>
                  this.setState({ createDeviationModalOpen: true })
                }
              />
              <Button title="Duplicate" onClick={() => onDuplicateOrder()} />
              <Button
                title={editingOrder ? 'Cancel edit order' : 'Edit order'}
                onClick={() => {
                  this.props.onEditOrder();
                }}
                disabled={!EDIT_IS_ENABLED.includes(order.status)}
              />
            </ButtonGroup>
          </div>
          <div style={{ height: '0.3em' }} />
          <div className="menu-bar__1">
            <ButtonGroup>
              <Button
                title="Mark as ready"
                onClick={() => this.markOrderAsReady()}
                loading={
                  markOrderAsync.orderAction === 'mark_as_ready' &&
                  markOrderAsync.status === ReqEnum.PENDING
                }
                disabled={
                  !MARK_AS_READY_IS_ENABLED.includes(order.status) ||
                  editingOrder
                }
              />
              <Button
                title="Mark as picked up"
                onClick={() => this.markOrderAsPicked()}
                loading={
                  markOrderAsync.orderAction === 'mark_as_picked' &&
                  markOrderAsync.status === ReqEnum.PENDING
                }
                disabled={
                  !MARK_AS_PICKED_UP_IS_ENABLED.includes(order.status) ||
                  editingOrder
                }
              />
              <Button
                title="Mark as delivered"
                onClick={() => this.markOrderAsDelivered()}
                loading={
                  markOrderAsync.orderAction === 'mark_as_delivered' &&
                  markOrderAsync.status === ReqEnum.PENDING
                }
                disabled={
                  !MARK_AS_DELIVERED_IS_ENABLED.includes(order.status) ||
                  editingOrder
                }
              />
            </ButtonGroup>

            <div className="mobile-only" style={{ height: 10 }} />
            <Button
              title="Cancel order"
              type="danger"
              disabled={
                !CANCEL_IS_ENABLED.includes(order.status) || editingOrder
              }
              onClick={this.onClickCancelOrder}
            />
          </div>
        </div>
        <ModalComponent
          title={'Create deviation'}
          showCloseButton={true}
          open={this.state.createDeviationModalOpen}
          setOpen={open => this.setState({ createDeviationModalOpen: open })}
        >
          <DeviationForm
            orderId={order.id}
            onSuccess={() => window.location.reload()}
          />
        </ModalComponent>
      </div>
    );
  }
}
